import { ref, Ref } from 'vue';
import { useStoryblokApi, ISbStoryData, ISbStoriesParams } from '@storyblok/vue';
import { TemplateMapLocationStoryblok } from '@/types';
import useConfig from '@/composables/useConfig';
import { DatasourceIcons, DatasourceMapCharacter, DatasourceMapVenueType } from '@/auto-generated/datasources';
import datasourceData from '@/auto-generated/datasources.json';
import { ExcludeUndefined } from '@/utils/typeUtils';

type ErrorObj = {
  status: number;
  message: string;
  response: string;
};
type State = 'INIT' | 'LOADING' | 'RESULTS' | 'NO_RESULTS' | 'ERROR';

const stories: Ref<ISbStoryData<TemplateMapLocationStoryblok>[] | null> =
ref(null);
const error: Ref<ErrorObj | null> = ref(null);
const loading = ref<boolean>(false);
const state = ref<State>('INIT');

const geoJson = computed(() => {
  if (!stories.value) return undefined;
  return generateGeoJson(stories.value);
});
export type GeoJson = ExcludeUndefined<typeof geoJson.value>

// const venueTypesInUse = computed(() => {
//   if (!stories.value) return [];
//   const categories = new Set<string>();
//   stories.value.forEach((location) => {
//     const venueType = location.content.venueType
//     if (venueType) categories.add(venueType.toString());
//   });
//   return Array.from(categories) as DatasourceMapVenueType[];
// })

// const charactersInUse = computed(() => {
//   if (!stories.value) return [];
//   const categories = new Set<string>();
//   stories.value.forEach((location) => {
//     const characters = location.content.characters
//     if (characters) categories.add(characters.toString());
//   });
//   return Array.from(categories) as DatasourceMapCharacter[];
// })



type FetchAllLocationsProps = {
  searchTerm?: ISbStoriesParams['search_term'];
}

export default function useMapGeojson() {

  const storyblokApi = useStoryblokApi();
  const { brand } = useConfig();
  
  const fetchAllLocations = async ({
    searchTerm
  }: FetchAllLocationsProps) => {
    try {
      // console.log('fetch locations - search:', searchTerm);
      loading.value = true;
      state.value = 'LOADING';
      
      let params = {
        version: import.meta.env.VITE_STORYBLOK_VERSION,
        starts_with: `${brand.value}/map`,
        content_type: 'TemplateMapLocation',
        search_term: searchTerm,
        per_page: 100,
        page: 1,
      };
      
      // calculating the needed pages via total information
      let firstResponse = await storyblokApi.get("cdn/stories", params);
      let lastPage = firstResponse.total
      ? Math.ceil(firstResponse.total / params.per_page)
      : 1;
      
      // retrieving the other pages
      let otherStories = [];
      for (let currentPage = 2; currentPage <= lastPage; currentPage++) {
        params.page = currentPage;
        otherStories.push((await storyblokApi.get("cdn/stories", params)).data.stories);
      }
      
      // merging all arrays
      let allStories = [firstResponse.data.stories, ...otherStories].flat();
      stories.value = allStories as ISbStoryData<TemplateMapLocationStoryblok>[];

      if (allStories.length <= 0) state.value = 'NO_RESULTS';
      else state.value = 'RESULTS';
      
      return allStories;
    }
    catch (err) {
      const errObj: ErrorObj = JSON.parse(err as string);
      error.value = errObj;
      state.value = 'ERROR';
      throw errObj;
    }
    finally {
      loading.value = false;
    }
  };
  
  return {
    fetchAllLocations,
    state,
    loading,
    error,
    stories,
    geoJson,
    // venueTypesInUse,
    // charactersInUse,
  };
}

export interface LocationGeoJsonProperties extends Pick<TemplateMapLocationStoryblok, 'title' | 'latitude' | 'longitude' | 'venueType' | 'characters'> {
  uuid: string;
  slug: string;
  full_slug: string;
  icon?: string;
  [key: string]: any;
}

const generateGeoJson = (locations: ISbStoryData<TemplateMapLocationStoryblok>[]) => {
  const features = locations.map((location) => {
    let venueType = location.content.venueType !== undefined ? location.content.venueType.toString() as DatasourceMapVenueType : undefined;
    const iconCharacter = location.content.characters?.toString();
    const feature: GeoJSON.Feature<GeoJSON.Point, LocationGeoJsonProperties> = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [parseFloat(location.content.longitude), parseFloat(location.content.latitude)],
      },
      properties: {
        uuid: location.uuid,
        slug: location.slug,
        full_slug: location.full_slug,
        title: location.content.title,
        latitude: location.content.latitude,
        longitude: location.content.longitude,
        venueType: venueType,
        characters: location.content.characters,
        icon: iconCharacter,
      },
    };
    return feature;
  });
  
  
  const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Point, LocationGeoJsonProperties> = {
    type: 'FeatureCollection',
    features,
  } 
  const geoJson = {
    type: 'geojson',
    data: featureCollection,
  };
  return geoJson
}
